export const APP_VERSION = '0.8.34';
export const RUNTIME_KEY = 'spookyPlans.runtime.v1';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e as BeforeInstallPromptEvent;
  });
}

export const getDeferredInstallPrompt = () => deferredInstallPrompt;
export const clearDeferredInstallPrompt = () => {
  deferredInstallPrompt = null;
};

type RuntimeState = {
  seenAppVersion: string;
  dismissedUpdateVersion: string | null;
  migrationCompleted: boolean;
};

export type PwaUpdate = {
  version: string;
  activate: () => void;
  dismiss: () => void;
};


const readRuntime = (): RuntimeState => {
  try {
    const stored = JSON.parse(localStorage.getItem(RUNTIME_KEY) ?? 'null') as Partial<RuntimeState> | null;
    return {
      seenAppVersion: typeof stored?.seenAppVersion === 'string' ? stored.seenAppVersion : APP_VERSION,
      dismissedUpdateVersion: typeof stored?.dismissedUpdateVersion === 'string' ? stored.dismissedUpdateVersion : null,
      migrationCompleted: stored?.migrationCompleted === true
    };
  } catch {
    return { seenAppVersion: APP_VERSION, dismissedUpdateVersion: null, migrationCompleted: false };
  }
};

const writeRuntime = (patch: Partial<RuntimeState>) => {
  const next = { ...readRuntime(), ...patch };
  localStorage.setItem(RUNTIME_KEY, JSON.stringify(next));
};

const workerVersion = (worker: ServiceWorker) => new Promise<string>((resolve) => {
  const channel = new MessageChannel();
  const timeout = window.setTimeout(() => resolve('new version'), 1500);
  channel.port1.onmessage = (event) => {
    window.clearTimeout(timeout);
    resolve(typeof event.data?.version === 'string' ? event.data.version : 'new version');
  };
  worker.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
});

export const registerPwa = async (
  onUpdate: (update: PwaUpdate | null) => void,
  onError: (message: string) => void
) => {
  if (!('serviceWorker' in navigator)) return () => undefined;

  try {
    writeRuntime({ seenAppVersion: APP_VERSION, migrationCompleted: true });
  } catch (error) {
    onError(error instanceof Error ? error.message : 'The local update preference could not be saved.');
  }
  let reloadRequested = false;
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

  const offerUpdate = async (worker: ServiceWorker) => {
    const version = await workerVersion(worker);
    if (readRuntime().dismissedUpdateVersion === version) return;
    onUpdate({
      version,
      activate: () => {
        reloadRequested = true;
        worker.postMessage({ type: 'SKIP_WAITING' });
      },
      dismiss: () => {
        try {
          writeRuntime({ dismissedUpdateVersion: version });
        } catch (error) {
          onError(error instanceof Error ? error.message : 'The update preference could not be saved.');
        }
        onUpdate(null);
      }
    });
  };

  const handleUpdateFound = () => {
    const installing = registration.installing;
    if (!installing) return;
    const handleStateChange = () => {
      if (installing.state === 'installed') {
        installing.removeEventListener('statechange', handleStateChange);
        if (navigator.serviceWorker.controller) void offerUpdate(installing);
      }
    };
    installing.addEventListener('statechange', handleStateChange);
  };

  const handleControllerChange = () => {
    if (reloadRequested) {
      reloadRequested = false;
      window.location.reload();
    }
  };

  registration.addEventListener('updatefound', handleUpdateFound);
  navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
  if (registration.waiting) await offerUpdate(registration.waiting);

  return () => {
    registration.removeEventListener('updatefound', handleUpdateFound);
    navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  };
};
